/**
 * Auth Provider Registry
 *
 * Provides a pluggable architecture for authentication providers.
 * Modules can register their auth provider implementations, and
 * the AuthProvider component will dynamically select the appropriate one.
 *
 * This pattern breaks circular dependencies between atom-client and auth modules.
 */

import type { ReactNode } from "react";
import type { ErrorScreen, NoAccessScreen } from "../FrontendProvision";

type UserRole = "admin" | "user";

/**
 * Admin actions interface for user management.
 *
 * These actions are implemented by auth providers that support admin functionality.
 * Not all providers support all actions - check for undefined before calling.
 *
 * @see https://www.better-auth.com/docs/plugins/admin
 */
export interface AuthAdminActions {
  /**
   * Create a new user.
   * @returns The ID of the created user
   */
  createUser?(data: {
    email: string;
    name: string;
    password: string;
    role?: UserRole;
  }): Promise<{ id: string }>;

  /**
   * Remove/delete a user.
   */
  removeUser?(userId: string): Promise<void>;

  /**
   * Ban a user, preventing them from signing in.
   * @param userId - The user to ban
   * @param reason - Optional reason for the ban
   * @param expiresIn - Optional expiration in seconds (undefined = permanent)
   */
  banUser?(userId: string, reason?: string, expiresIn?: number): Promise<void>;

  /**
   * Unban a user, allowing them to sign in again.
   */
  unbanUser?(userId: string): Promise<void>;

  /**
   * Set a user's role.
   */
  setUserRole?(userId: string, role: UserRole): Promise<void>;

  /**
   * Revoke all active sessions for a user.
   */
  revokeUserSessions?(userId: string): Promise<void>;

  /**
   * Impersonate a user (start an impersonation session).
   * @param userId - Auth provider user id (Better Auth user id)
   * @param atomUserId - Atom Framework user id for context routing after reload
   */
  impersonateUser?(userId: string, atomUserId?: string): Promise<void>;

  /**
   * Stop impersonating and return to the admin session.
   */
  stopImpersonation?(): Promise<void>;

  /**
   * Check if the current session is an impersonation session.
   */
  isImpersonating?(): boolean;

  /**
   * Send a password reset email to a user.
   * Uses the auth provider's password reset flow.
   */
  sendPasswordResetEmail?(email: string): Promise<void>;

  /**
   * Send an invitation link to a user via magic link.
   * Used to invite new users who will complete onboarding.
   *
   * @param options - Invitation options
   * @param options.email - The email address to send the invitation to
   * @param options.callbackURL - URL to redirect after magic link verification (for existing users)
   * @param options.newUserCallbackURL - URL to redirect for new users (onboarding page)
   * @param options.name - Optional display name for the user (for email personalization)
   * @returns The result from the magic link sign-in API
   */
  sendInviteLink?(options: {
    email: string;
    callbackURL?: string;
    newUserCallbackURL?: string;
    name?: string;
  }): Promise<{ success: boolean }>;
}

/**
 * Props passed to auth provider components
 */
export interface AuthProviderComponentProps {
  /** Child components to render */
  children: ReactNode;
  /** Splash screen shown during loading */
  splashScreen: ReactNode;
  /** Error screen shown on auth errors */
  errorScreen: ErrorScreen;
  /** Screen shown when authenticated users have no app access */
  noAccessScreen: NoAccessScreen;
  /** Auth settings from backend */
  settings: unknown;
  /**
   * Function to get the auth token.
   * Provider should call this to set up the token getter.
   */
  setGetAuthToken: (getter: () => Promise<string | null>) => void;
  /**
   * Wrapper component for universal auth context.
   * Provider should wrap children with this.
   */
  UniversalAuthWrapper: React.ComponentType<{ children: ReactNode }>;
}

/**
 * Factory interface for creating auth providers
 */
export interface AuthProviderFactory {
  /**
   * Unique identifier for this auth type.
   * Must match the `type` field from backend settings.
   * E.g., "better-auth", "oidc", "keycloak", "zitadel"
   */
  type: string;

  /**
   * Check if this factory can handle the given settings.
   * Called to determine which provider to use.
   */
  matches: (settings: unknown) => boolean;

  /**
   * Create the auth provider component.
   * Returns a React element that wraps children with auth context.
   */
  createProvider: (props: AuthProviderComponentProps) => ReactNode;

  /**
   * Create the universal auth context wrapper.
   * This provides the unified auth interface for useUniversalAuth().
   */
  createUniversalAuthWrapper: () => React.ComponentType<{
    children: ReactNode;
  }>;

  /**
   * Create a token getter function from settings.
   * Used to get auth tokens for API calls.
   */
  createTokenGetter: (
    settings: unknown,
    baseURL: string,
  ) => () => Promise<string | null>;

  /**
   * Create admin actions for user management.
   * Returns null if admin functionality is not supported or not enabled.
   *
   * @param settings - Auth settings from the backend
   * @param authClient - The auth client instance (type varies by provider)
   */
  createAdminActions?: (
    settings: unknown,
    authClient: unknown,
  ) => AuthAdminActions | null;
}

/**
 * Registry of auth provider factories
 */
const authProviderRegistry: AuthProviderFactory[] = [];

/**
 * Register an auth provider factory.
 * Call this at module initialization to register an auth provider.
 *
 * @example
 * ```ts
 * // In better-auth-module/frontend/register.ts
 * registerAuthProvider({
 *   type: "better-auth",
 *   matches: (s) => s?.type === "better-auth",
 *   createProvider: (props) => <BetterAuthProvider {...props} />,
 *   // ...
 * });
 * ```
 */
export function registerAuthProvider(factory: AuthProviderFactory): void {
  // Check for duplicates
  const existing = authProviderRegistry.find((f) => f.type === factory.type);
  if (existing) {
    console.warn(
      `[AuthProviderRegistry] Overwriting existing provider for type "${factory.type}"`,
    );
    const index = authProviderRegistry.indexOf(existing);
    authProviderRegistry[index] = factory;
  } else {
    authProviderRegistry.push(factory);
    console.log(`[AuthProviderRegistry] Registered provider: ${factory.type}`);
  }
}

/**
 * Find an auth provider factory that matches the given settings.
 *
 * @param settings - Auth settings from the backend
 * @returns The matching factory, or undefined if none found
 */
export function findAuthProvider(
  settings: unknown,
): AuthProviderFactory | undefined {
  return authProviderRegistry.find((factory) => factory.matches(settings));
}

/**
 * Get all registered auth providers.
 * Useful for debugging.
 */
export function getRegisteredProviders(): readonly AuthProviderFactory[] {
  return authProviderRegistry;
}

/**
 * Check if any auth providers are registered.
 */
export function hasRegisteredProviders(): boolean {
  return authProviderRegistry.length > 0;
}

/**
 * Clear all registered providers.
 * Mainly useful for testing.
 */
export function clearAuthProviders(): void {
  authProviderRegistry.length = 0;
}

/**
 * Current admin actions instance.
 * Set by the active auth provider when admin features are enabled.
 */
let currentAdminActions: AuthAdminActions | null = null;

/**
 * Set the current admin actions.
 * Called by auth providers when they initialize admin functionality.
 */
export function setAdminActions(actions: AuthAdminActions | null): void {
  currentAdminActions = actions;
}

/**
 * Get the current admin actions.
 * Returns null if admin functionality is not available.
 */
export function getAdminActions(): AuthAdminActions | null {
  return currentAdminActions;
}
